import { computed, ref, watch } from 'vue';
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router';
import { get } from 'lodash';
import { useI18n, useMainMenuComplexTabsMeta, useTableQuery, useUrl } from '@/composables';
import { $emitter } from '@/services/emitter';
import type { ReportTab } from '@/views/reports/views/report-tab.types';
import { useStore } from '@/store';

export type { ReportTab } from '@/views/reports/views/report-tab.types';

export const useReportTabs = () => {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const { t } = useI18n();
  const tableQueryApi = useTableQuery();
  const { tabsMeta } = useMainMenuComplexTabsMeta();
  const {
    getDashboardLink,
    getHomeLink,
    isDashboard,
    isSettingsRoute,
    normalizeUrl,
  } = useUrl();

  const lastActiveTab = ref<ReportTab | null>(null);

  const homeProps = computed(() => ({
    name: t('Home'),
    icon: 'fas fa-home',
  }));

  const dashboardProps = computed(() => ({
    name: t('Dashboard'),
    icon: 'fas fa-tachometer-alt-fast',
  }));

  const settingsProps = computed(() => ({
    name: t('Settings'),
    icon: 'fas fa-cog',
  }));

  const activeReports = computed(() =>
    tableQueryApi.tableActiveReports.value.filter((item) => item),
  );

  const getTabName = (item: Record<string, unknown>) => {
    const drillName = (item.drill as Array<{ name?: string; nameFull?: string }> | undefined)?.[0];
    return (item.title as string)
      || drillName?.nameFull
      || drillName?.name
      || (item.nameFull as string)
      || (item.name as string)
      || t('Please select a tab');
  };

  const currentTab = computed(() =>
    activeReports.value.find(
      (item) => tableQueryApi.tableActiveTab.value === item.tabId,
    ),
  );

  const returnToLastPage = () => {
    const newTab = activeReports.value.find(
      (item) => item.tabId === tableQueryApi.tableActiveTab.value,
    );
    const url = newTab
      ? newTab.url
      : tableQueryApi.tableActiveTab.value === -1 && tableQueryApi.tableSettingsTabRoute.value
        ? tableQueryApi.tableSettingsTabRoute.value
        : getHomeLink();

    router.push(url);
  };

  const closeTabBase = (tabId: number) => {
    const activeTab = tableQueryApi.tableActiveTab.value;
    store.closeTableTab(tabId);

    if (activeTab === tabId) {
      returnToLastPage();
      return true;
    }

    return false;
  };

  const closeTab = (event: MouseEvent, tabId: number) => {
    event.stopPropagation();
    return closeTabBase(tabId);
  };

  const closeSettingsTab = () => {
    store.setSettingsTabRoute(null);
    store.closeTableTab(-1);

    if (isSettingsRoute.value) {
      returnToLastPage();
    }
  };

  const clickTab = (url: RouteLocationRaw, tabId?: number) => {
    const resolvedUrl = router.resolve(url).fullPath;
    const isSameTab = tabId !== undefined && tableQueryApi.tableActiveTab.value === tabId;
    const isSameUrl = resolvedUrl === route.fullPath;

    if (isSameTab && isSameUrl) return;

    if (tabId !== undefined) {
      const tableActiveTabData: { tabId: number; url?: string } = { tabId };

      if (tableQueryApi.tableActiveTab.value) {
        tableActiveTabData.url = route.fullPath;
      }

      store.navigateTableActiveTab(tableActiveTabData);
    }

    if (isSameUrl) {
      $emitter.emit('reload-report');
      return;
    }

    router.push(url);
  };

  const dashboardTab = computed<ReportTab[]>(() => [{
    key: 'dashboard',
    title: dashboardProps.value.name,
    icon: dashboardProps.value.icon,
    active: isDashboard.value,
    click: () => clickTab(getDashboardLink(true), 0),
    close: null,
    hideIfMany: false,
    hasBookmarks: false,
    link: null,
  }]);

  const homeReportTab = computed<ReportTab[]>(() => {
    const active = !tableQueryApi.tableActiveTab.value
      && !isSettingsRoute.value
      && !isDashboard.value;

    return [{
      key: 'home',
      title: homeProps.value.name,
      icon: homeProps.value.icon,
      active,
      click: () => clickTab(getHomeLink(), 0),
      close: null,
      hideIfMany: false,
      hasBookmarks: false,
      link: null,
    }];
  });

  const settingsReportTabBase = computed<ReportTab>(() => ({
    key: 'settings',
    title: settingsProps.value.name,
    icon: settingsProps.value.icon,
    active: isSettingsRoute.value,
    click: () => {
      const settingsRoute = tableQueryApi.tableSettingsTabRoute.value;
      if (settingsRoute) clickTab(settingsRoute, -1);
    },
    close: () => closeSettingsTab(),
    hideIfMany: true,
    hasBookmarks: false,
    link: null,
  }));

  const settingsReportTab = computed<ReportTab[]>(() =>
    tableQueryApi.tableSettingsTabRoute.value ? [settingsReportTabBase.value] : [],
  );

  const commonReportTab = computed<ReportTab[]>(() =>
    activeReports.value.map<ReportTab>((data, index) => ({
      key: data.tabId ?? data.id ?? data.name,
      index,
      title: getTabName(data as Record<string, unknown>),
      icon: tabsMeta.value[data.filterTables as string]
        ? tabsMeta.value[data.filterTables as string].icon
        : data.icon,
      active: Boolean(currentTab.value && currentTab.value.tabId === data.tabId),
      click: () => clickTab(data.url, data.tabId),
      close: (event) => closeTab(event as MouseEvent, data.tabId),
      link: data.url,
      hideIfMany: true,
      hasBookmarks: true,
      meta: {
        tabId: data.tabId,
        mouseEvents: true,
        index,
        item: data as Record<string, unknown>,
      },
    })),
  );

  const reportTabsClosable = computed(() =>
    settingsReportTab.value.concat(commonReportTab.value),
  );

  const reportTabs = computed(() =>
    dashboardTab.value.concat(homeReportTab.value).concat(reportTabsClosable.value),
  );

  watch(
    currentTab,
    (value) => {
      if (value) {
        lastActiveTab.value = commonReportTab.value.find(
          (item) => value.tabId === item.meta?.tabId,
        ) ?? null;
        return;
      }
      if (!lastActiveTab.value && commonReportTab.value.length) {
        lastActiveTab.value = commonReportTab.value[commonReportTab.value.length - 1];
      }
    },
    { immediate: true },
  );

  const currentCommonTabPropsNoHome = computed(() => {
    if (isSettingsRoute.value) return settingsProps.value;

    const groupingTab = lastActiveTab.value
      ?? commonReportTab.value[commonReportTab.value.length - 1]
      ?? settingsReportTab.value[0]
      ?? null;
    const groupingTabItem = get(groupingTab, 'meta.item', {}) as Record<string, unknown>;

    return {
      name: (groupingTab?.title as string) || getTabName(groupingTabItem),
      icon: tabsMeta.value[groupingTabItem.filterTables as string]
        ? tabsMeta.value[groupingTabItem.filterTables as string].icon
        : ((groupingTabItem.icon as string) || groupingTab?.icon),
    };
  });

  const currentCommonTabProps = computed(() =>
    tableQueryApi.tableActiveTab.value || isSettingsRoute.value
      ? currentCommonTabPropsNoHome.value
      : isDashboard.value
        ? dashboardProps.value
        : homeProps.value,
  );

  const activeReportsUrls = computed(() =>
    activeReports.value.map((item) => {
      const url = item.tabId === tableQueryApi.tableActiveTab.value
        ? route.fullPath
        : item.url;

      return normalizeUrl(url);
    }),
  );

  const bookmarkIndexes = computed(() => {
    const bookmarkValues = store.bookmarks.map((item) => normalizeUrl(item.url));

    return activeReportsUrls.value.map((reportUrl) =>
      bookmarkValues.findIndex((bookmarkUrl) => reportUrl === bookmarkUrl),
    );
  });

  const activeReportsLength = computed(() => activeReports.value.length);

  const commonTabsLength = computed(() => {
    let result = activeReportsLength.value;
    if (tableQueryApi.tableSettingsTabRoute.value) {
      result = result + 1;
    }
    return result;
  });

  const isManyReports = computed(() => activeReportsLength.value > 4);

  const hasCommonTab = computed(
    () => activeReports.value.length > 0 || Boolean(tableQueryApi.tableSettingsTabRoute.value),
  );

  const closeAllTabs = (event: MouseEvent) => {
    event.stopPropagation();

    activeReports.value.forEach((item) => {
      store.closeTableTab(item.tabId);
    });

    closeSettingsTab();
  };

  return {
    activeReports,
    currentTab,
    lastActiveTab,
    reportTabs,
    reportTabsClosable,
    bookmarkIndexes,
    currentCommonTabProps,
    currentCommonTabPropsNoHome,
    commonTabsLength,
    isManyReports,
    hasCommonTab,
    clickTab,
    closeTab,
    closeAllTabs,
  };
};
